import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  BsBuilding,
  BsPeopleFill,
  BsDownload,
  BsBookmark,
  BsArrowLeft,
  BsArrowRight,
  BsShieldLock,
} from 'react-icons/bs';
import { BsInfoCircleFill } from 'react-icons/bs';
import { TbLoader2 } from 'react-icons/tb';

import styles from './Onboarding.module.scss';
import { CreateCompany } from './components/CreateCompany.component';
import { CreateEmployee } from './components/CreateEmployee.component';
import { DownloadDesktopApp } from './components/DownloadDesktopApp.component';
import { OnboardingSidebar } from './components/OnboardingSidebar.component';

import firebase from '@config/firebase.config';
import httpClient from '@/config/httpClient';
import teamDatabase from '@/services/team';
import { teamActionTypes } from '@store/Team/Team.actionTypes';
import { authActionTypes } from '@/store/Auth/Auth.actionTypes';
import { findPendingInvitesForEmail } from '@/services/invitesClient';

import {
  initialOnboardingStepsData,
  onboardingSteps,
  stepCardConfig,
  stepNumberForFormMapping,
  stepNumberMapping,
} from './Onboarding.constants';
import {
  setFormDataForCreateCompany,
  setFormDataForCreateEmployee,
  isFormDataChanged,
} from './Onboarding.helpers';

const database = firebase.firestore();
const ADMIN_ROLE = 'Admin';

// Per-step icon rendered inside the card header circle
const STEP_ICONS = {
  1: <BsBuilding size={26} color="#8B9EB7" />,
  2: <BsPeopleFill size={26} color="#8B9EB7" />,
  3: <BsDownload size={26} color="#8B9EB7" />,
};

export const OnboardingComponent = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const profile = useSelector((state) => state.auth?.profile);
  const pendingInvitesEmail = profile?.email?.trim().toLowerCase() || '';

  const { data: pendingInvitesForEmail, isLoading: pendingInvitesLoading } =
    useQuery(
      ['invites', 'pendingForEmail', pendingInvitesEmail],
      () => findPendingInvitesForEmail(pendingInvitesEmail),
      {
        enabled:
          !!pendingInvitesEmail &&
          profile?.onboardingData?.onboardingCompleted !== true,
        staleTime: 30 * 1000,
      }
    );

  useEffect(() => {
    if (
      pendingInvitesForEmail?.length > 0 &&
      profile?.onboardingData?.onboardingCompleted !== true
    ) {
      history.replace('/invitations-hub');
    }
  }, [
    pendingInvitesForEmail,
    history,
    profile?.onboardingData?.onboardingCompleted,
  ]);

  const [loading, setloading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState({
    createCompanyError: null,
    createTeamFormError: null,
    createEmployeeFormError: null,
  });
  const [createCompanyForm, setCreateCompanyForm] = useState({
    ...initialOnboardingStepsData.createCompanyForm,
  });
  const [createEmployeeForm, setCreateEmployeeForm] = useState({
    ...initialOnboardingStepsData.createEmployeeForm,
  });
  const onboardingStepsDataRef = useRef({ ...initialOnboardingStepsData });

  useEffect(() => {
    if (profile?.onboardingData?.onboardingCompleted) {
      const cid = localStorage.getItem('cid');
      history.push(`/dashboard-overview?cid=${cid}`);
    }
  }, [profile?.onboardingData?.onboardingCompleted]);

  useEffect(() => {
    let step = 1;
    if (profile?.onboardingData?.onboardingSteps?.createCompany) {
      const form = setFormDataForCreateCompany(profile);
      onboardingStepsDataRef.current.createCompanyForm = form;
      setCreateCompanyForm(form);
    }
    if (profile?.onboardingData?.onboardingSteps?.createEmployee) {
      const form = setFormDataForCreateEmployee(profile);
      onboardingStepsDataRef.current.createEmployeeForm = form;
      setCreateEmployeeForm(form);
      step = 2;
    }
    setCurrentStep(step);
  }, [profile?.onboardingData?.onboardingSteps?.createCompany]);

  // ── Navigation ──────────────────────────────────────────────
  const navigateToNextStep = () => {
    if (currentStep < onboardingSteps.length) setCurrentStep((s) => s + 1);
  };

  const navigateToPreviousStep = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  // ── Save + continue logic ────────────────────────────────────
  const isSaveRequired = () => {
    if (currentStep !== 1) return true;
    return isFormDataChanged({
      orignalData:
        onboardingStepsDataRef.current[stepNumberForFormMapping[currentStep]],
      newData: createCompanyForm,
      currentStep,
    });
  };

  const saveDataForCurrentStep = async () => {
    if (!isSaveRequired()) {
      navigateToNextStep();
      return;
    }

    const currentStepKey = stepNumberMapping[currentStep];
    let dataToSave = {};

    if (currentStep === 1) {
      dataToSave = {
        companyName: createCompanyForm.companyName,
        companyLocation: createCompanyForm.companyLocation?.value,
        timezone: createCompanyForm.timezone?.value,
        phoneNumber: createCompanyForm.phoneNumber,
        startTime: createCompanyForm.startTime,
        endTime: createCompanyForm.endTime,
      };
    } else if (currentStep === 2) {
      dataToSave = { employees: createEmployeeForm.employees };
    }

    try {
      setloading(true);
      const response = await httpClient.post('/saveOnboardingSteps', {
        userUid: profile?.id,
        currentStepKey,
        onboardingStepData: dataToSave,
      });
      if (response.data.success) navigateToNextStep();
    } catch (err) {
      console.log(err, '>>> Onboarding.component.jsx <<<');
    } finally {
      setloading(false);
    }
  };

  const validateCreateEmployeeForm = () => {
    const errors = [];
    const emailSet = new Set();
    const deskIdSet = new Set();

    createEmployeeForm?.employees?.forEach((emp, index) => {
      if (!emp.email) errors.push(`Row ${index + 1}: Email is required`);
      if (!emp.name) errors.push(`Row ${index + 1}: Name is required`);
      if (!emp.team) errors.push(`Row ${index + 1}: Team is required`);
      if (!emp.timeZone) errors.push(`Row ${index + 1}: TimeZone is required`);
      if (!emp.role) errors.push(`Row ${index + 1}: Role is required`);

      if (emp.email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emp.email))
          errors.push(`Row ${index + 1}: Invalid email format`);
        if (emailSet.has(emp.email.toLowerCase()))
          errors.push(`Row ${index + 1}: Duplicate email in form`);
        emailSet.add(emp.email.toLowerCase());
      }

      if (emp.deskId != null && String(emp.deskId).trim() !== '') {
        const trimmedDeskId = String(emp.deskId).trim();
        if (deskIdSet.has(trimmedDeskId))
          errors.push(`Row ${index + 1}: Duplicate deskId (${trimmedDeskId})`);
        deskIdSet.add(trimmedDeskId);
      }
    });

    if (errors.length > 0) {
      setError({ ...error, createEmployeeFormError: errors.join('\n') });
    } else {
      setError({ ...error, createEmployeeFormError: null });
      saveDataForCurrentStep();
    }
  };

  const handleFinish = async () => {
    let companyRefId = null;
    let isBatchCompleted = false;
    let teamDocId = null;

    try {
      const groupedEmployees = createEmployeeForm.employees.reduce(
        (acc, emp) => {
          if (!acc[emp.team]) acc[emp.team] = { members: [] };
          acc[emp.team].members.push({ ...emp, team: emp.team });
          return acc;
        },
        {}
      );

      setloading(true);
      const batch = database.batch();
      const companyRef = database.collection('companyDatabase').doc();
      companyRefId = companyRef.id;
      const memberRef = database.collection('membersDatabase').doc(profile?.id);

      Object.keys(groupedEmployees).forEach((teamName) => {
        const team = groupedEmployees[teamName];
        team.teamRef = database.collection('teamDatabase').doc();
        if (!teamDocId) teamDocId = team.teamRef.id;

        batch.set(
          team.teamRef,
          {
            teamName,
            ownedBy: companyRef.id,
            createdBy: profile?.id,
            memberDetails: {
              [profile?.id]: {
                userEmailid: profile?.email,
                userName: profile?.name,
                userType: ADMIN_ROLE,
              },
            },
          },
          { merge: true }
        );

        batch.set(
          database
            .collection('companyDatabase')
            .doc(companyRef.id)
            .collection('teams')
            .doc(team.teamRef.id),
          { teamId: team.teamRef.id },
          { merge: true }
        );

        batch.set(
          memberRef
            .collection('companyList')
            .doc(companyRef.id)
            .collection('teamList')
            .doc(team.teamRef.id),
          {
            Role: ADMIN_ROLE,
            Team: teamName,
            email: profile?.email,
            activeTeamid: team.teamRef.id,
          },
          { merge: true }
        );
      });

      const trialExpiryDate = new Date();
      trialExpiryDate.setDate(trialExpiryDate.getDate() + 14);

      batch.set(
        companyRef,
        {
          name: createCompanyForm.companyName,
          location: createCompanyForm.companyLocation?.value,
          timeZone: createCompanyForm.timezone?.value,
          screenshot: true,
          createdBy: profile?.id,
          admins: [profile?.id],
          subscriptions: {
            trialExpiryDate: trialExpiryDate.toISOString(),
            isTrialActive: true,
            occupiedSeats: 1,
          },
        },
        { merge: true }
      );

      batch.set(
        memberRef.collection('companyList').doc(companyRef.id),
        {
          role: ADMIN_ROLE,
          companyName: createCompanyForm.companyName,
          companyId: companyRef.id,
        },
        { merge: true }
      );

      batch.update(companyRef, {
        teams: Object.keys(groupedEmployees).map(
          (t) => groupedEmployees[t].teamRef.id
        ),
      });

      await batch.commit();
      isBatchCompleted = true;

      for (const teamName in groupedEmployees) {
        const team = groupedEmployees[teamName];
        const validEmployees = team.members.filter(
          (emp) => emp.email || emp.name || emp.timeZone || emp.role
        );
        if (validEmployees.length > 0) {
          const user = firebase.auth().currentUser;
          const idToken = await user.getIdToken();
          await httpClient.post(
            '/sendAddEmployeeInvites',
            {
              companyId: companyRef.id,
              teamId: team.teamRef.id,
              employees: validEmployees,
              source: 'form',
            },
            { headers: { Authorization: `Bearer ${idToken}` } }
          );
        }
      }
    } catch (err) {
      console.log(err, '>>> Onboarding.component.jsx <<<', 'handleFinish');
    } finally {
      if (isBatchCompleted) {
        await database
          .collection('membersDatabase')
          .doc(profile?.id)
          .update({ 'onboardingData.onboardingCompleted': true });

        const functions = firebase.functions();
        await teamDatabase.updateMemberActiveCompanyAndTeam(
          profile?.id,
          companyRefId,
          teamDocId
        );

        functions.httpsCallable('sendEmailForNewCompanyCreated')({
          userEmail: profile?.email,
          userName: profile?.name,
          companyName: createCompanyForm.companyName,
          companyId: companyRefId,
          phoneNumber: createCompanyForm.phoneNumber,
        });

        functions.httpsCallable('sendEmailForNewUserSignup')({
          userEmail: profile?.email,
          userName: profile?.name,
        });

        dispatch({
          type: teamActionTypes.SWITCH_COMPANY_ACTIVE_TEAM,
          payload: { companyId: companyRefId, userId: profile?.id },
        });

        setTimeout(() => {
          history.push(`/dashboard-overview?cid=${companyRefId}`);
        }, 1000);
      }
      setloading(false);
    }
  };

  // ── Button state ─────────────────────────────────────────────
  const isNextButtonDisabled = () => {
    if (currentStep === 1) {
      return (
        !createCompanyForm.companyName ||
        !createCompanyForm.companyLocation?.value ||
        !createCompanyForm.timezone?.value
      );
    }
    return false;
  };

  const handleContinueClick = () => {
    if (currentStep === 2) {
      validateCreateEmployeeForm();
      return;
    }
    if (currentStep === onboardingSteps.length) {
      handleFinish();
    } else {
      saveDataForCurrentStep();
    }
  };

  // ── Step content ─────────────────────────────────────────────
  const getCurrentStepComponent = () => {
    switch (currentStep) {
      case 1:
        return (
          <CreateCompany
            form={createCompanyForm}
            setForm={setCreateCompanyForm}
          />
        );
      case 2:
        return (
          <CreateEmployee
            form={createEmployeeForm}
            setForm={setCreateEmployeeForm}
            error={error.createEmployeeFormError}
            onSkip={navigateToNextStep}
          />
        );
      case 3:
        return (
          <DownloadDesktopApp
            onFinish={handleFinish}
            onBack={navigateToPreviousStep}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  // ── Loading guard ────────────────────────────────────────────
  if (
    pendingInvitesLoading &&
    pendingInvitesEmail &&
    profile?.onboardingData?.onboardingCompleted !== true
  ) {
    return null;
  }

  const continueLabel =
    currentStep === onboardingSteps.length ? 'Finish' : 'Continue Setup';

  // ── Render ───────────────────────────────────────────────────
  return (
    <div className={styles.onboardingLayout}>
      {/* Left marketing sidebar */}
      <OnboardingSidebar currentStep={currentStep} />

      {/* Right form area */}
      <main className={styles.main}>
        <div className={styles.card}>
          {/* ── Stepper ── */}
          <div className={styles.stepperWrapper}>
            <div className={styles.stepper}>
              {onboardingSteps.map((step, index) => {
                const stepNum = index + 1;
                const isActive = currentStep === stepNum;
                const isCompleted = currentStep > stepNum;
                return (
                  <React.Fragment key={step.key}>
                    <div className={styles.stepperItem}>
                      <div
                        className={classNames(styles.stepperCircle, {
                          [styles.stepperCircleActive]: isActive,
                          [styles.stepperCircleCompleted]: isCompleted,
                        })}
                      >
                        {stepNum}
                      </div>
                      <span
                        className={classNames(styles.stepperLabel, {
                          [styles.stepperLabelActive]: isActive,
                          [styles.stepperLabelCompleted]: isCompleted,
                        })}
                      >
                        {step.title}
                      </span>
                    </div>
                    {index < onboardingSteps.length - 1 && (
                      <div
                        className={classNames(styles.stepperConnector, {
                          [styles.stepperConnectorDone]: isCompleted,
                        })}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* ── Divider ── */}
          <hr className={styles.cardDivider} />

          {/* ── Card header ── */}
          <div className={styles.cardHeader}>
            <div className={styles.cardHeaderIcon}>
              {STEP_ICONS[currentStep]}
            </div>
            <div className={styles.cardHeaderMeta}>
              <h2 className={styles.cardHeaderTitle}>
                {stepCardConfig[currentStep].title}
              </h2>
              <p className={styles.cardHeaderSubtitle}>
                {stepCardConfig[currentStep].subtitle}
              </p>
            </div>
          </div>

          {/* ── Divider ── */}
          <hr className={styles.cardDivider} />

          {/* ── Form body ── */}
          <div className={styles.cardBody}>{getCurrentStepComponent()}</div>

          {/* ── Info notice (step 1 only) ── */}
          {currentStep === 1 && (
            <div className={styles.cardInfoWrapper}>
              <div className={styles.cardInfo}>
                <BsInfoCircleFill size={15} className={styles.cardInfoIcon} />
                <span>
                  You can change these details anytime from{' '}
                  <a href="#" className={styles.cardInfoLink}>
                    Workspace
                  </a>{' '}
                  Settings.
                </span>
              </div>
            </div>
          )}

          {/* ── Card footer — hidden on step 3 (component manages its own footer) ── */}
          {currentStep !== 3 && (
            <div
              className={`${styles.cardFooter}${currentStep === 1 ? ` ${styles.cardFooterRight}` : ''}`}
            >
              {/* Left action */}
              {currentStep === 1 ? null : (
                // <button
                //   type="button"
                //   className={styles.saveLaterBtn}
                //   onClick={() => {}}
                // >
                //   <BsBookmark size={14} />
                //   Save & Continue Later
                // </button>
                <button
                  type="button"
                  className={styles.backBtn}
                  onClick={navigateToPreviousStep}
                >
                  <BsArrowLeft size={16} />
                  Back
                </button>
              )}

              {/* Right action */}
              <button
                type="button"
                className={styles.continueBtn}
                onClick={handleContinueClick}
                disabled={isNextButtonDisabled() || loading}
              >
                {loading ? (
                  <TbLoader2 size={18} className={styles.continueBtnSpinner} />
                ) : (
                  <>
                    {continueLabel}
                    <BsArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Trust badge */}
        <div className={styles.trustBadge}>
          <BsShieldLock size={14} />
          <span>Your data is secure and encrypted</span>
        </div>
      </main>
    </div>
  );
};
